import { TFunction } from "i18next";

import {
  FilterDefinition,
  FilterDefinitionType,
  SchemaAnalyticsConfig,
  SchemaDashboardView,
  SchemaDefinition,
} from "shared/api/schemaDefinitions/types";
import {
  compareBySidebarOrder,
  getVisibleDashboardViews,
} from "shared/api/schemaDefinitions/utils";
import { capitalizeFirstLetter } from "shared/utils";

import {
  convertDateDropdownOptionToDateRangeFilter,
  getDateDropdownOptionText,
} from "pages/shared/Dashboard/DateFieldDropdown/utils";
import { AnyDateDropdownOptions } from "pages/shared/Dashboard/types";
import {
  DashboardFilterSchemaContext,
  flattenDashboardFilterFields,
} from "pages/shared/schemaDefinitions/dashboardFilterFields";

import { getDateSummaryLabelKey } from "features/ui/Filters/utils";

import { DashboardGrouping } from "./types";

export interface AggregatedView {
  view: SchemaDashboardView;
  schemaDef: SchemaDefinition;
}

// Tab order comes from each view's own dashboardOrder, so views from different
// schemas can interleave. The pre-sort by sidebar order is the tiebreak: views
// sharing an order (including the 0 default) keep the schema order the sidebar
// uses, because Array#sort is stable.
export const getOrderedDashboardViews = (
  schemaDefs: SchemaDefinition[],
  isSuperAdmin: boolean
): AggregatedView[] =>
  [...schemaDefs]
    .sort(compareBySidebarOrder)
    .flatMap((schemaDef) =>
      getVisibleDashboardViews(schemaDef, isSuperAdmin).map((view) => ({
        view,
        schemaDef,
      }))
    )
    .sort(
      (a, b) => (a.view.dashboardOrder ?? 0) - (b.view.dashboardOrder ?? 0)
    );

export const getSchemaDashboardViewPageKey = (
  schemaName: string,
  versionKey: string
) => `schema-dashboard-${schemaName}-${versionKey}`;

export interface SchemaDashboardTabKey {
  schemaName: string;
  versionKey: string;
}

// ":" is unambiguous as the delimiter because encodeURIComponent escapes it
// inside the encoded parts.
const TAB_KEY_SEPARATOR = ":";

// Builds the ?tab= key identifying a dashboard view, parsed back by Duck's
// app-state util. Always change both functions together.
export const buildSchemaDashboardTabKey = (
  schemaName: string,
  versionKey: string,
  index: number
): string =>
  [schemaName, versionKey, String(index)]
    .map(encodeURIComponent)
    .join(TAB_KEY_SEPARATOR);

export const parseSchemaDashboardTabKey = (
  tab: string
): SchemaDashboardTabKey | undefined => {
  const segments = tab.split(TAB_KEY_SEPARATOR);
  if (segments.length !== 3) {
    return undefined;
  }

  try {
    const [schemaName, versionKey] = segments.map(decodeURIComponent);

    return schemaName && versionKey ? { schemaName, versionKey } : undefined;
  } catch {
    // Malformed percent-encoding — not a key this module built.
    return undefined;
  }
};

/**
 * Maps each date filter's row (see getDateSummaryLabelKey) to a formatted label
 * for its selected dropdown option (e.g. "This month").
 */
export const buildDateFieldSummaryLabels = (
  definitions: FilterDefinition[],
  dateOptions: Record<string, AnyDateDropdownOptions | undefined>,
  eventContext: DashboardFilterSchemaContext,
  assetContext: DashboardFilterSchemaContext,
  t: TFunction
): Record<string, string> => {
  const labels: Record<string, string> = {};

  flattenDashboardFilterFields(definitions, eventContext, assetContext, t, {
    dedupe: false,
  })
    .filter((field) => field.type === FilterDefinitionType.DATE)
    .forEach((field) => {
      const option = dateOptions[field.definitionId] ?? field.defaultDateOption;
      if (!option) return;

      const row = convertDateDropdownOptionToDateRangeFilter(option, field.key);
      labels[getDateSummaryLabelKey(field.key, row)] = capitalizeFirstLetter(
        getDateDropdownOptionText(option, t)
      );
    });

  return labels;
};

// Both grouping modes run the same analytics query, which needs these three fields. A view may
// override the date field, so callers that have a view pass it in.
export const hasGroupingAnalyticsFields = (
  analyticsConfig: SchemaAnalyticsConfig | undefined,
  chartDateField?: string
): boolean =>
  Boolean(
    analyticsConfig?.assetName &&
    (chartDateField || analyticsConfig?.dateField) &&
    analyticsConfig?.assetBirthdayField
  );

export const resolveDashboardGrouping = ({
  view,
  hasSmartGrouping,
  hasAnalyticsFields,
}: {
  view: SchemaDashboardView;
  hasSmartGrouping: boolean;
  hasAnalyticsFields: boolean;
}): DashboardGrouping => {
  const smartGroupsAllowed =
    (view.enableSmartGroups ?? false) && hasSmartGrouping && hasAnalyticsFields;

  const attributeGrouping =
    !smartGroupsAllowed && view.attributeGrouping && hasAnalyticsFields
      ? view.attributeGrouping
      : undefined;

  return {
    groupingAllowed: smartGroupsAllowed || Boolean(attributeGrouping),
    attributeGrouping,
  };
};
